import React, { useState, useEffect, ReactNode } from 'react';
import { TenantContext } from '../contexts/TenantContext';
import { TenantInfo } from '../interfaces/tenant.interface';
import TenantService from '../services/tenant.service';

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(true);

  const validateDomain = async (domain: string) => {
    try {
      setIsValidating(true);
      const response = await TenantService.validateDomain({ domain });

      if (response.data) {
        const tenant: TenantInfo = {
          tenant_id: response.data.tenant_id,
          name: response.data.name,
          code: response.data.code,
          logo_path: response.data.logo_path,
        };

        setTenantInfo(tenant);
        TenantService.storeTenantInfo(tenant);
        setIsValidating(false);
      }
    } catch (error: any) {
      setTenantInfo(null);
      setIsValidating(false);
      window.location.href = '/error/tenant-invalid';
    }
  };

  useEffect(() => {
    const storedTenant = TenantService.getTenantInfo();

    if (storedTenant) {
      setTenantInfo(storedTenant);
      setIsValidating(false);
    } else {
      if (window.location.pathname !== '/error/tenant-invalid') {
        const domain = window.location.host;
        validateDomain(domain);
      } else {
        setIsValidating(false);
      }
    }
  }, []);

  useEffect(() => {
    if (tenantInfo) {
      document.title = tenantInfo.name;

      const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (favicon && tenantInfo.logo_path) {
        favicon.href = tenantInfo.logo_path;
      } else if (tenantInfo.logo_path) {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = tenantInfo.logo_path;
        document.head.appendChild(newFavicon);
      }
    }
  }, [tenantInfo]);

  return (
    <TenantContext.Provider
      value={{
        tenantInfo,
        isValidating,
        validateDomain,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

